/**
 * Jackpot Animation Configuration
 * Defines which jackpot sections have dynamic (animated) minor and mini levels
 * Most sections have static MINOR/MINI that rarely change
 */

interface JackpotAnimationConfig {
  /** Sections where MINOR and MINI levels change frequently and should animate */
  sectionsWithDynamicMinorMini: string[];

  /** Levels that are always animated regardless of section */
  alwaysAnimatedLevels: number[];
}

const JACKPOT_ANIMATION_CONFIG: JackpotAnimationConfig = {
  // Only Clover Chance (MYSTERY section) has frequently changing MINOR/MINI values
  sectionsWithDynamicMinorMini: [
    'clover chance',
    'Clover Chance',
    'CLOVER CHANCE'
  ],

  // GRAND (1) and MAJOR (2) are always animated as they change frequently
  alwaysAnimatedLevels: [1, 2]
};

/**
 * Check if a specific jackpot level should be treated as static (no animation)
 * @param jackpotName - Name of the jackpot section
 * @param level - Level number (1=GRAND, 2=MAJOR, 3=MINOR, 4=MINI)
 */
export function isStaticJackpotLevel(jackpotName: string, level?: number): boolean {
  // No level specified means it's a game card - never static
  if (level === undefined) {
    return false;
  }

  // GRAND and MAJOR are never static
  if (JACKPOT_ANIMATION_CONFIG.alwaysAnimatedLevels.includes(level)) {
    return false;
  }

  // For MINOR (3) and MINI (4), they are static unless in the dynamic list
  const normalizedName = jackpotName.toLowerCase();
  const isDynamic = JACKPOT_ANIMATION_CONFIG.sectionsWithDynamicMinorMini.some(
    section => section.toLowerCase() === normalizedName
  );

  // Static if NOT in the dynamic list
  return !isDynamic;
}

/**
 * Check if a jackpot should show animation for a given value change
 * Static levels snap to exact values, dynamic levels animate
 */
// fallow-ignore-next-line unused-export
export function shouldShowAnimation(jackpotName: string, valueDifference: number, level?: number): boolean {
  const isStatic = isStaticJackpotLevel(jackpotName, level);

  if (isStatic) {
    // Static jackpots: never animate, always show exact value
    return false;
  }

  // Dynamic jackpots: animate for any meaningful change
  return valueDifference > 0.01;
}
